import * as React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { FileDropzone } from './commons/fileDropzone/FileDropzone'
import { useState } from 'react'
import { Alert, AlertTitle, CircularProgress, Typography } from '@mui/material'
import FileDropzoneAttachmentPreview from './commons/fileDropzone/FileDropzoneAttachmentPreview'
import { EStandard } from '../services/enums/EStandard'
import { getStandard } from '../services/getStandard'

interface UploadFileDialogProps {
    onFileContent: (content: string, forceStandard?: EStandard, sampleRate?: number) => void
    onFileName: (name: string | undefined) => void
    onStandard: (standard: EStandard) => void
    isNotCancellable?: boolean
}

export default function UploadFileDialog({
    onFileContent,
    onFileName,
    onStandard,
    isNotCancellable,
}: UploadFileDialogProps) {
    const [open, setOpen] = useState(true)
    const [loading, setLoading] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>()
    const [selectedFile, setSelectedFile] = useState<File>()
    const [selectedFileContent, setSelectedFileContent] = useState<string>()

    const handleClickOpen = () => {
        setOpen(true)
    }
    const handleClose = (event?: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
        if (isNotCancellable && reason === 'backdropClick') {
            return
        }

        setOpen(false)
        resetDialog()
    }

    const handleUpload = () => {
        if (selectedFileContent) {
            onFileContent(selectedFileContent)
            onFileName(selectedFile?.name)
        }
        handleClose()
    }

    const resetDialog = () => {
        setSelectedFile(undefined)
        setSelectedFileContent(undefined)
        setErrorMessage(undefined)
    }

    const onDropHandler = ({ length, 0: file }: File[]) => {
        setLoading(true)
        if (length === 0) {
            // No accepted files
            setErrorMessage('No files were accepted')
            return
        }

        setSelectedFile(file)

        const reader = new FileReader()

        reader.onerror = (error) => {
            console.error('Error: ', error)
        }
        reader.onabort = (error) => {
            console.error('Error: ', error)
        }

        reader.onload = async (event: ProgressEvent<FileReader>) => {
            if (!event?.target?.result) {
                setErrorMessage('File seems to be empty')
            } else {
                setSelectedFileContent(event?.target?.result as string)
                const standard = getStandard(event?.target?.result as string)
                if (standard) {
                    onStandard(standard)
                }
            }
            setLoading(false)
        }

        reader.readAsText(file)
    }

    return (
        <>
            <Button variant='contained' onClick={handleClickOpen}>
                New logfile
            </Button>
            <Dialog maxWidth='xl' open={open} onClose={handleClose} disableEscapeKeyDown={isNotCancellable}>
                <DialogTitle>Upload Logfile</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            height: '70vh',
                            width: '70vw',
                        }}
                    >
                        {loading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    height: '100%',
                                    width: '100%',
                                }}
                            >
                                <CircularProgress disableShrink />
                                <Typography sx={{ p: 2 }}>Loading file content...</Typography>
                            </Box>
                        ) : (
                            <FileDropzone
                                onDrop={(files) => onDropHandler(files as File[])}
                                selectedFile={selectedFile}
                                selectedFileContent={selectedFileContent}
                                fileType='.log, .txt'
                                filesAccepted={{ 'text/plain': ['.log', '.txt', 'text/plain'] }}
                                previewComponent={
                                    <FileDropzoneAttachmentPreview file={selectedFile} onClose={() => resetDialog()} />
                                }
                            />
                        )}
                        {errorMessage && (
                            <Alert severity='error' sx={{ width: '100%', mt: 2 }} onClose={resetDialog}>
                                <AlertTitle>Invalid File</AlertTitle>
                                Please try uploading another file. {errorMessage}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
                    {isNotCancellable ? (
                        // So that the space-between works
                        <span></span>
                    ) : (
                        <Button variant='contained' color='secondary' onClick={handleClose}>
                            Cancel
                        </Button>
                    )}
                    <Button variant='contained' onClick={handleUpload} disabled={!selectedFileContent}>
                        Process
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
